import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";
import { initHeaderOverlayController } from "./header-overlay-controller.js";
import { initDrawerOverlayController } from "./drawer-overlay-controller.js";
import { QuantitySelectorComponent } from "./quantity-selector.js";
import { updateCartBubble } from "./utilities.js";
import "./lazy-image.js";

// Cart Drawer Component sitewide event

class CartDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Prevent initial flicker while syncing state
    const prevVisibility = this.style.visibility;
    this.style.visibility = 'hidden';

    this.overlay = document.querySelector("[data-site-overlay]");
    this.toggleButtons = document.querySelectorAll("[data-drawer-toggle]");
    this.closeButtons = this.querySelectorAll("[data-drawer-close]");
    // Default to closed unless explicitly open
    this.isOpen = this.classList.contains("translate-x-0");
    this.removeButtons = document.querySelectorAll("[data-cart-remove]");

    this.#syncOverlayState();
    this.#addEventListeners();
    this.#attachRemoveListeners();
    this.#attachCloseListeners();
    this.#attachUpsellListeners();

    // Ensure overlay reflects open state without direct DOM manipulation
    if (this.isOpen) {
      ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    }

    // Reveal after sync and mark as synced to avoid first-frame animations
    requestAnimationFrame(() => {
      this.dataset.synced = 'true';
      this.style.visibility = prevVisibility;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    ThemeBus.addEventListener(EVENTS.cartDrawerOpen, this.#handleOpen);
    ThemeBus.addEventListener(EVENTS.cartDrawerClose, this.#handleClose);
    ThemeBus.addEventListener(EVENTS.cartDrawerToggle, this.#handleToggle);
    ThemeBus.addEventListener(EVENTS.cartItemAdded, this.#handleItemAdded);
    ThemeBus.addEventListener(EVENTS.cartUpdated, this.#handleCartUpdated);

    this.toggleButtons.forEach((btn) =>
      btn.addEventListener("click", this.#handleToggle)
    );

    if (this.overlay) {
      this.overlay.addEventListener("click", this.#handleClose);
    }
  }

  #attachCloseListeners() {
    const closeButtons = this.querySelectorAll("[data-drawer-close]");
    closeButtons.forEach((button) => {
      button.addEventListener("click", this.#handleClose);
    });
  }

  #attachRemoveListeners() {
    const removeButtons = this.querySelectorAll("[data-cart-remove]");

    removeButtons.forEach((button) => {
      button.addEventListener("click", async () => {
        const line = Number(button.dataset.line);
        if (!line) return;

        try {
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ line, quantity: 0 }),
          });

          if (!res.ok) throw new Error("Remove failed");
          const updatedCart = await res.json();

          window.__cart = updatedCart;
          ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
        } catch (err) {
          console.error("Error removing item from cart:", err);
        }
      });
    });
  }

  /**
   * Attaches upsell form listener using event delegation.
   * Works with Swiper's cloned slides since the listener is on the parent component.
   */
  #attachUpsellListeners() {
    this.addEventListener('submit', this.#handleUpsellFormSubmit);
  }

  /**
   * Handles form submission events, filtering for upsell forms only.
   */
  #handleUpsellFormSubmit = (event) => {
    const form = event.target.closest('.cart-upsell__form');
    if (!form) return;
    
    event.preventDefault();
    this.#handleUpsellAddToCart(form);
  };

  /**
   * Adds an upsell product to the cart via AJAX.
   * @param {HTMLFormElement} form - The upsell form element
   */
  #handleUpsellAddToCart = async (form) => {
    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantityInput = form.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = parseInt(quantityInput?.value) || 1;
    const button = form.querySelector('[data-cart-upsell-add]');

    if (!variantId || !button) return;

    const originalText = button.textContent;
    this.#setButtonState(button, true, 'Adding...');

    try {
      const requestBody = this.#buildCartRequestBody(form, variantId, quantity);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error('Failed to add item to cart');

      const item = await response.json();
      try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.cartItemAdded, { detail: item })); } catch (_) {}

      this.#setButtonState(button, true, 'Added!');
      setTimeout(() => {
        if (quantityInput) quantityInput.value = 1;
        this.#setButtonState(button, false, originalText);
      }, 1000);
    } catch (error) {
      console.error('Error adding upsell item:', error);
      this.#setButtonState(button, false, originalText);
    }
  };

  /**
   * Builds the request body for cart add, including any line item properties.
   */
  #buildCartRequestBody(form, variantId, quantity) {
    const properties = {};
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const match = input.name.match(/properties\[(.*)\]/);
      if (match) properties[match[1]] = input.value;
    });

    const body = { id: variantId, quantity };
    if (Object.keys(properties).length > 0) body.properties = properties;
    return body;
  }

  /**
   * Updates button disabled state and text.
   */
  #setButtonState(button, disabled, text) {
    button.disabled = disabled;
    button.textContent = text;
  }

  #handleCartUpdated = async () => {
    await this.#renderCartContents();
    updateCartBubble();
  };

  async #renderCartContents() {
    try {
      // Use the cart.js endpoint to get the latest cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Store the latest cart data
      window.__cart = cartData;
      
      // Fetch the rendered cart drawer section
      const response = await fetch(`/?section_id=cart-drawer&v=${Date.now()}`, { cache: 'no-store' });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");
      
      // Find the section content (Shopify wraps sections in a div)
      const sectionContent = html.querySelector('.shopify-section') || html.body;
      
      // Update cart items
      const newContent = sectionContent.querySelector("[data-cart-drawer-contents]");
      const currentContainer = this.querySelector("[data-cart-drawer-contents]");

      if (newContent && currentContainer) {
        currentContainer.innerHTML = newContent.innerHTML;
        this.#attachRemoveListeners();
        this.#attachCloseListeners();
      } else {
        console.warn('[CartDrawer] Could not find cart contents elements');
      }

      // Update total price
      const newTotalPrice = sectionContent.querySelector("[data-cart-total-price]");
      const currentTotalPrice = this.querySelector("[data-cart-total-price]");

      if (newTotalPrice && currentTotalPrice) {
        currentTotalPrice.textContent = newTotalPrice.textContent;
      } else {
        console.warn('[CartDrawer] Could not find total price elements', {
          newTotalPrice: !!newTotalPrice,
          currentTotalPrice: !!currentTotalPrice
        });
      }

      // Update entire footer to catch discount changes (includes upsell swiper)
      const newFooter = sectionContent.querySelector("cart-drawer-footer");
      const currentFooter = this.querySelector("cart-drawer-footer");

      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
        // Dispatch event for swiper re-initialization after footer is updated
        document.dispatchEvent(new CustomEvent('cart:content-updated'));
      }
    } catch (error) {
      console.error("Error rendering cart contents:", error);
    }
  }

  #removeEventListeners() {
    ThemeBus.removeEventListener(EVENTS.cartDrawerOpen, this.#handleOpen);
    ThemeBus.removeEventListener(EVENTS.cartDrawerClose, this.#handleClose);
    ThemeBus.removeEventListener(EVENTS.cartDrawerToggle, this.#handleToggle);
    ThemeBus.removeEventListener(EVENTS.cartItemAdded, this.#handleItemAdded);
    ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#handleCartUpdated);

    this.toggleButtons.forEach((btn) =>
      btn.removeEventListener("click", this.#handleToggle)
    );

    if (this.overlay) {
      this.overlay.removeEventListener("click", this.#handleClose);
    }
  }

  #handleOpen = () => {
    // Guard against multiple opens (prevents overlay counter from incrementing twice)
    if (this.isOpen) return;
    
    this.isOpen = true;
    this.classList.remove("translate-x-full");
    this.classList.add("translate-x-0");
    document.body.classList.add("overflow-hidden");
    // Also show drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
  };

  #handleClose = () => {
    this.isOpen = false;

    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.classList.remove("translate-x-0");
    this.classList.add("translate-x-full");
    document.body.classList.remove("overflow-hidden");
    // Also hide drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  };

  #handleToggle = (event) => {
    // Prevent default behavior (form submission, etc.) and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  };

  #handleItemAdded = async () => {
    await this.#renderCartContents();
    updateCartBubble();
    this.#handleOpen();
  };

  #syncOverlayState() {
    // Avoid animating on initial sync
    const prevTransition = this.style.transition;
    this.style.transition = 'none';
    if (this.isOpen) {
      this.classList.add("translate-x-0");
      this.classList.remove("translate-x-full");
    } else {
      this.classList.remove("translate-x-0");
      this.classList.add("translate-x-full");
    }
    // Force reflow then restore transitions
    void this.offsetHeight;
    this.style.transition = prevTransition;
  }
}

/**
 * ModalDrawerComponent
 * 
 * A generic drawer component that can house any content.
 * Supports multiple instances on a single page, each with a unique ID.
 * 
 * Features:
 * - Integrates with site-wide overlay (drawer-overlay system)
 * - Can be triggered via data attributes or ThemeBus events
 * - Supports multiple drawers on the same page
 * - Closes on overlay click, close button, or Escape key
 * - Prevents body scroll when open
 * - Uses ThemeBus for event communication
 * - Slides in from right (like cart drawer) or left (configurable)
 * 
 * Usage:
 * 
 * HTML Structure:
 * <modal-drawer-component data-modal-drawer-id="unique-drawer-id" data-drawer-position="right">
 *   <div class="drawer-header">
 *     <button data-modal-drawer-close>Close</button>
 *   </div>
 *   <div class="drawer-content">
 *     <!-- Your generic content here -->
 *   </div>
 * </modal-drawer-component>
 * 
 * Trigger button (anywhere on page):
 * <button data-modal-drawer-toggle="unique-drawer-id">Open Drawer</button>
 * 
 * Or via JavaScript:
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalDrawerOpen, { detail: { id: 'unique-drawer-id' } }));
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalDrawerClose, { detail: { id: 'unique-drawer-id' } }));
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalDrawerToggle, { detail: { id: 'unique-drawer-id' } }));
 */
class ModalDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Prevent initial flicker while syncing state
    const prevVisibility = this.style.visibility;
    this.style.visibility = 'hidden';

    // Initialize configuration
    this.drawerId = this.dataset.modalDrawerId;
    if (!this.drawerId) {
      console.warn('[ModalDrawer] Modal drawer component missing data-modal-drawer-id attribute', this);
    }

    this.position = this.dataset.drawerPosition || 'right';
    this.isOpen = this.classList.contains('translate-x-0');

    // Cache element references
    this.overlay = document.querySelector('[data-site-overlay]');
    this.toggleButtons = this.#getToggleButtons();

    // Initialize state and event listeners
    this.#syncInitialState();
    this.#addEventListeners();

    // Ensure overlay reflects open state
    if (this.isOpen) {
      try { ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow)); } catch (_) {}
    }

    // Reveal after sync to avoid first-frame animations
    requestAnimationFrame(() => {
      this.dataset.synced = 'true';
      this.style.visibility = prevVisibility;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  /**
   * Gets toggle buttons for this drawer instance
   * @returns {NodeList} Collection of toggle buttons
   */
  #getToggleButtons() {
    if (this.drawerId) {
      return document.querySelectorAll(`[data-modal-drawer-toggle="${this.drawerId}"]`);
    }
    return document.querySelectorAll('[data-modal-drawer-toggle]');
  }

  /**
   * Gets CSS classes for open/closed states based on position
   * @returns {{open: string, closed: string}} Object with open and closed class names
   */
  #getPositionClasses() {
    if (this.position === 'left') {
      return { open: 'translate-x-0', closed: '-translate-x-full' };
    }
    return { open: 'translate-x-0', closed: 'translate-x-full' };
  }

  /**
   * Updates drawer visibility classes
   * @param {boolean} isOpen - Whether drawer should be open
   */
  #updateDrawerClasses(isOpen) {
    const classes = this.#getPositionClasses();
    if (isOpen) {
      this.classList.remove(classes.closed);
      this.classList.add(classes.open);
    } else {
      this.classList.remove(classes.open);
      this.classList.add(classes.closed);
    }
  }

  /**
   * Syncs initial state without animation
   */
  #syncInitialState() {
    const prevTransition = this.style.transition;
    this.style.transition = 'none';
    this.#updateDrawerClasses(this.isOpen);
    void this.offsetHeight; // Force reflow
    this.style.transition = prevTransition;
  }

  #addEventListeners() {
    // ThemeBus events for programmatic control
    if (this.drawerId) {
      try { ThemeBus.addEventListener(EVENTS.modalDrawerOpen, this.#handleGlobalOpen); } catch (_) {}
      try { ThemeBus.addEventListener(EVENTS.modalDrawerClose, this.#handleGlobalClose); } catch (_) {}
      try { ThemeBus.addEventListener(EVENTS.modalDrawerToggle, this.#handleGlobalToggle); } catch (_) {}
    }

    // Toggle button clicks
    this.toggleButtons.forEach(btn => btn.addEventListener('click', this.#handleToggle));

    // Overlay click (close on backdrop)
    if (this.overlay) {
      this.overlay.addEventListener('click', this.#handleOverlayClick);
    }

    // Close button clicks (delegated)
    this.addEventListener('click', this.#handleCloseButtonClick);

    // Escape key
    document.addEventListener('keydown', this.#handleEscapeKey);
  }

  #removeEventListeners() {
    if (this.drawerId) {
      try { ThemeBus.removeEventListener(EVENTS.modalDrawerOpen, this.#handleGlobalOpen); } catch (_) {}
      try { ThemeBus.removeEventListener(EVENTS.modalDrawerClose, this.#handleGlobalClose); } catch (_) {}
      try { ThemeBus.removeEventListener(EVENTS.modalDrawerToggle, this.#handleGlobalToggle); } catch (_) {}
    }

    this.toggleButtons.forEach(btn => btn.removeEventListener('click', this.#handleToggle));

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.#handleOverlayClick);
    }

    this.removeEventListener('click', this.#handleCloseButtonClick);
    document.removeEventListener('keydown', this.#handleEscapeKey);
  }

  #handleGlobalOpen = (event) => {
    if (!this.drawerId || event.detail?.id === this.drawerId) {
      this.#handleOpen();
    }
  };

  #handleGlobalClose = (event) => {
    if (!this.drawerId || event.detail?.id === this.drawerId) {
      this.#handleClose();
    }
  };

  #handleGlobalToggle = (event) => {
    if (!this.drawerId || event.detail?.id === this.drawerId) {
      this.#handleToggle();
    }
  };

  #handleToggle = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  };

  #handleCloseButtonClick = (event) => {
    const closeButton = event.target.closest('[data-modal-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#handleClose();
    }
  };

  #handleOverlayClick = (event) => {
    if (!this.isOpen) return;

    // Only close if clicking directly on overlay, not inside any drawer
    const isOverlayClick = event.target === this.overlay || 
                          event.target.closest('[data-site-overlay]') === this.overlay;
    
    if (isOverlayClick) {
      const clickedDrawer = event.target.closest(
        'modal-drawer-component, cart-drawer-component, header-drawer-component, mobile-filter-drawer-component'
      );
      if (!clickedDrawer || clickedDrawer === this) {
        this.#handleClose();
      }
    }
  };

  #handleEscapeKey = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.#handleClose();
    }
  };

  #handleOpen() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.#updateDrawerClasses(true);
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    try { ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow)); } catch (_) {}
    this.#focusFirstElement();
  }

  #handleClose() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.#updateDrawerClasses(false);
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    // Remove focus from drawer if it has focus
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    try { ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide)); } catch (_) {}
  }

  #focusFirstElement() {
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const firstElement = this.querySelector(focusableSelector);
    if (firstElement) {
      firstElement.focus();
    }
  }
}

class HeaderDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.overlay = document.querySelector('[data-site-overlay]');
    this.toggleButtons = document.querySelectorAll('[data-header-drawer-toggle]');
    
    // Ensure drawer starts in closed state
    this.isOpen = this.classList.contains('translate-x-0');
    
    // Force closed state if not explicitly open
    if (!this.isOpen) {
      this.classList.add('-translate-x-full');
      this.classList.remove('translate-x-0');
      this.setAttribute('aria-hidden', 'true');
      this.#disableFocusableElements();
    } else {
      this.#enableFocusableElements();
    }

    this.#addEventListeners();

    // Ensure overlay reflects open state
    if (this.isOpen) {
      ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    this.toggleButtons.forEach(btn =>
      btn.addEventListener('click', this.#handleToggle)
    );

    ThemeBus.addEventListener(EVENTS.headerDrawerOpen, this.#handleOpen);
    ThemeBus.addEventListener(EVENTS.headerDrawerClose, this.#handleClose);
    ThemeBus.addEventListener(EVENTS.headerDrawerToggle, this.#handleToggle);

    this.overlay?.addEventListener('click', this.#handleClose);
  }

  #removeEventListeners() {
    this.toggleButtons.forEach(btn =>
      btn.removeEventListener('click', this.#handleToggle)
    );

    ThemeBus.removeEventListener(EVENTS.headerDrawerOpen, this.#handleOpen);
    ThemeBus.removeEventListener(EVENTS.headerDrawerClose, this.#handleClose);
    ThemeBus.removeEventListener(EVENTS.headerDrawerToggle, this.#handleToggle);

    this.overlay?.removeEventListener('click', this.#handleClose);
  }

  #handleOpen = () => {
    // Only allow open when screen is below lg breakpoint (1024px)
    if (window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }
    this.isOpen = true;
    this.classList.remove('-translate-x-full');
    this.classList.add('translate-x-0');
    this.setAttribute('aria-hidden', 'false');
    this.#enableFocusableElements();
    document.body.classList.add('overflow-hidden');
    // Show drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
  }

  #handleClose = () => {
    this.isOpen = false;

    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.classList.remove('translate-x-0');
    this.classList.add('-translate-x-full');
    this.setAttribute('aria-hidden', 'true');
    this.#disableFocusableElements();
    document.body.classList.remove('overflow-hidden');
    // Hide drawer overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  }

  /**
   * Disables focusable elements when drawer is hidden
   */
  #disableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );
    focusableElements.forEach(el => {
      el.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Re-enables focusable elements when drawer is shown
   */
  #enableFocusableElements() {
    const focusableElements = this.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex="-1"]'
    );
    focusableElements.forEach(el => {
      el.removeAttribute('tabindex');
    });
  }

  #handleToggle = () => {
    // Only allow toggle when screen is below lg breakpoint (1024px)
    // This matches the lg:hidden class on the hamburger button
    if (window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  }
}

/**
 * HeaderDrawerMenu Component
 * 
 * Handles multi-level navigation with slide-in transitions
 * - Manages panel visibility and transitions for submenus
 * - Syncs with main drawer open/close state via MutationObserver
 * - Resets to main menu when drawer closes
 * - Uses ThemeBus for accessibility announcements
 */
class HeaderDrawerMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // State
    this.currentLevel = 'main'; // main, submenu, or grandchild
    this.activeSubmenuIndex = null;
    this.activeGrandchildIndex = null;

    // Elements
    this.mainPanel = this.querySelector('[data-menu-level="main"]');
    this.submenuPanels = this.querySelectorAll('[data-menu-level="submenu"]');
    this.grandchildPanels = this.querySelectorAll('[data-menu-level="grandchild"]');
    this.submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
    this.grandchildTriggers = this.querySelectorAll('[data-grandchild-trigger]');
    this.submenuBackButtons = this.querySelectorAll('[data-submenu-back]');
    this.grandchildBackButtons = this.querySelectorAll('[data-grandchild-back]');

    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  #init() {
    // Ensure all panels start in correct position
    this.#initializePanelPositions();

    // Bind submenu triggers
    this.submenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.#openSubmenu(e));
    });

    // Bind grandchild triggers
    this.grandchildTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.#openGrandchild(e));
    });

    // Bind submenu back buttons
    this.submenuBackButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.#closeSubmenu(e));
    });

    // Bind grandchild back buttons
    this.grandchildBackButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.#closeGrandchild(e));
    });

    // Bind all menu links to close drawer when clicked
    this.#attachLinkListeners();

    // Watch for drawer closing to reset menu
    // Use MutationObserver to detect when parent drawer closes
    const drawerComponent = this.closest('header-drawer-component');
    if (drawerComponent) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'aria-hidden') {
            const isHidden = drawerComponent.getAttribute('aria-hidden') === 'true';
            if (isHidden) {
              this.#resetMenu();
            }
          }
        });
      });

      this.observer.observe(drawerComponent, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }
  }

  /**
   * Attaches click listeners to all menu links to close drawer
   */
  #attachLinkListeners() {
    const allLinks = this.querySelectorAll('a[href]:not([href="#"])');
    allLinks.forEach((link) => {
      link.addEventListener('click', () => {
        // Close the drawer via ThemeBus
        try {
          ThemeBus.dispatchEvent(new Event(EVENTS.headerDrawerClose));
        } catch (_) {}
      });
    });
  }

  /**
   * Ensures all panels are in correct initial position
   */
  #initializePanelPositions() {
    // Main panel should be visible and centered
    if (this.mainPanel) {
      this.mainPanel.classList.add('translate-x-0');
      this.mainPanel.classList.remove('-translate-x-full');
      this.mainPanel.setAttribute('aria-hidden', 'false');
    }

    // All submenu panels should be off-screen to the right
    this.submenuPanels.forEach((panel) => {
      panel.classList.add('translate-x-full');
      panel.classList.remove('translate-x-0', '-translate-x-full');
      panel.setAttribute('aria-hidden', 'true');
    });

    // All grandchild panels should be off-screen to the right
    this.grandchildPanels.forEach((panel) => {
      panel.classList.add('translate-x-full');
      panel.classList.remove('translate-x-0', '-translate-x-full');
      panel.setAttribute('aria-hidden', 'true');
    });

    // All triggers should be collapsed
    this.submenuTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.grandchildTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Opens a submenu panel with slide-in animation
   */
  #openSubmenu(event) {
    const trigger = event.currentTarget;
    const submenuIndex = trigger.dataset.submenuTrigger;
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    if (!submenuPanel) return;

    // Update state
    this.currentLevel = 'submenu';
    this.activeSubmenuIndex = submenuIndex;

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');
    submenuPanel.setAttribute('aria-hidden', 'false');

    // Animate: slide main panel left, slide submenu in from right
    this.mainPanel.classList.remove('translate-x-0');
    this.mainPanel.classList.add('-translate-x-full');

    submenuPanel.classList.remove('translate-x-full');
    submenuPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation(`Viewing ${trigger.textContent.trim()} menu`);
  }

  /**
   * Closes active submenu and returns to main menu
   */
  #closeSubmenu(event) {
    const button = event.currentTarget;
    const submenuIndex = button.dataset.submenuBack;
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);
    const trigger = this.querySelector(`[data-submenu-trigger="${submenuIndex}"]`);

    if (!submenuPanel) return;

    // Update state
    this.currentLevel = 'main';
    this.activeSubmenuIndex = null;

    // Update ARIA
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    submenuPanel.setAttribute('aria-hidden', 'true');

    // Animate: slide submenu right, slide main panel back to center
    submenuPanel.classList.remove('translate-x-0');
    submenuPanel.classList.add('translate-x-full');

    this.mainPanel.classList.remove('-translate-x-full');
    this.mainPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation('Back to main menu');
  }

  /**
   * Opens a grandchild panel with slide-in animation
   */
  #openGrandchild(event) {
    const trigger = event.currentTarget;
    const grandchildIndex = trigger.dataset.grandchildTrigger;
    const grandchildPanel = this.querySelector(`#grandchild-${grandchildIndex}`);

    if (!grandchildPanel) return;

    // Update state
    this.currentLevel = 'grandchild';
    this.activeGrandchildIndex = grandchildIndex;

    // Update ARIA
    trigger.setAttribute('aria-expanded', 'true');
    grandchildPanel.setAttribute('aria-hidden', 'false');

    // Find the current submenu panel
    const [submenuIndex] = grandchildIndex.split('-');
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    // Animate: slide submenu left, slide grandchild in from right
    if (submenuPanel) {
      submenuPanel.classList.remove('translate-x-0');
      submenuPanel.classList.add('-translate-x-full');
    }

    grandchildPanel.classList.remove('translate-x-full');
    grandchildPanel.classList.add('translate-x-0');

    // Announce to screen readers
    this.#announceNavigation(`Viewing ${trigger.textContent.trim()} menu`);
  }

  /**
   * Closes active grandchild and returns to submenu
   */
  #closeGrandchild(event) {
    const button = event.currentTarget;
    const grandchildIndex = button.dataset.grandchildBack;
    const grandchildPanel = this.querySelector(`#grandchild-${grandchildIndex}`);
    const trigger = this.querySelector(`[data-grandchild-trigger="${grandchildIndex}"]`);

    if (!grandchildPanel) return;

    // Update state
    this.currentLevel = 'submenu';
    this.activeGrandchildIndex = null;

    // Update ARIA
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    grandchildPanel.setAttribute('aria-hidden', 'true');

    // Find the parent submenu panel
    const [submenuIndex] = grandchildIndex.split('-');
    const submenuPanel = this.querySelector(`#submenu-${submenuIndex}`);

    // Animate: slide grandchild right, slide submenu back to center
    grandchildPanel.classList.remove('translate-x-0');
    grandchildPanel.classList.add('translate-x-full');

    if (submenuPanel) {
      submenuPanel.classList.remove('-translate-x-full');
      submenuPanel.classList.add('translate-x-0');
    }

    // Announce to screen readers
    this.#announceNavigation('Back to submenu');
  }

  /**
   * Resets menu to initial state (called when drawer closes)
   */
  #resetMenu() {
    // Return to main panel immediately (no animation since drawer is closed)
    if (this.currentLevel !== 'main') {
      // Reset all panels
      this.mainPanel.classList.remove('-translate-x-full');
      this.mainPanel.classList.add('translate-x-0');

      this.submenuPanels.forEach((panel) => {
        panel.classList.remove('translate-x-0', '-translate-x-full');
        panel.classList.add('translate-x-full');
        panel.setAttribute('aria-hidden', 'true');
      });

      this.grandchildPanels.forEach((panel) => {
        panel.classList.remove('translate-x-0', '-translate-x-full');
        panel.classList.add('translate-x-full');
        panel.setAttribute('aria-hidden', 'true');
      });

      // Reset triggers
      this.submenuTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      
      this.grandchildTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Reset state
      this.currentLevel = 'main';
      this.activeSubmenuIndex = null;
      this.activeGrandchildIndex = null;
    }

    // Scroll main panel back to top
    const mainPanelScroller = this.mainPanel?.querySelector('.overflow-y-auto');
    if (mainPanelScroller) {
      mainPanelScroller.scrollTop = 0;
    }
  }

  /**
   * Announces navigation changes to screen readers
   */
  #announceNavigation(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    this.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

class MobileFilterDrawerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Prevent initial flicker while syncing state
    const prevVisibility = this.style.visibility;
    this.style.visibility = 'hidden';

    this.overlay = document.querySelector('[data-site-overlay]');
    this.toggleButtons = document.querySelectorAll('[data-mobile-filter-drawer-toggle]');
    // Default to closed unless explicitly open
    this.isOpen = this.classList.contains('translate-x-0');

    this.#syncOverlayState();
    this.#addEventListeners();
    // Reveal after sync and mark as synced
    requestAnimationFrame(() => {
      this.dataset.synced = 'true';
      this.style.visibility = prevVisibility;
    });
  }

  /**
   * Helper: Get the desktop sort select (excludes mobile one)
   */
  #getDesktopSortSelect() {
    const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
    return Array.from(allSortSelects).find(select => 
      select.id !== 'sort-by-mobile' && !select.closest('mobile-filter-drawer-content')
    );
  }

  /**
   * Helper: Check if a sort value exists in select options
   */
  #isValidSortOption(select, value) {
    if (!select || !value) return false;
    return Array.from(select.options).some(opt => opt.value === value);
  }

  /**
   * Helper: Get default sort value (prefers 'manual', falls back to first option)
   */
  #getDefaultSortValue(sortSelect) {
    if (!sortSelect || sortSelect.options.length === 0) return null;
    
    // Try to find 'manual' (featured) option first
    const manualOption = Array.from(sortSelect.options).find(opt => opt.value === 'manual');
    if (manualOption) return 'manual';
    
    // Fall back to first option
    return sortSelect.options[0].value;
  }

  /**
   * Helper: Sync sort select from URL, preserving user selections
   */
  #syncSortSelectFromUrl(sortSelect, urlParams) {
    if (!sortSelect) return;

    const sortValue = urlParams.get('sort_by');
    const currentValue = sortSelect.value;
    const userSelected = sortSelect.dataset.userSelected === 'true';

    // Preserve user selection if they've manually selected something not yet in URL
    if (userSelected && currentValue && !sortValue) {
      return;
    }

    if (sortValue) {
      // Update from URL if value exists and is different
      if (this.#isValidSortOption(sortSelect, sortValue) && sortValue !== currentValue) {
        sortSelect.value = sortValue;
        delete sortSelect.dataset.userSelected;
      }
    } else if (!currentValue && !userSelected) {
      // Set default (prefers 'manual', falls back to first option)
      const defaultValue = this.#getDefaultSortValue(sortSelect);
      if (defaultValue) {
        sortSelect.value = defaultValue;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #addEventListeners() {
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', this.#handleToggle);
    });

    this.overlay?.addEventListener('click', this.#handleClose);
    
    // Add clear filters functionality
    const clearButton = document.getElementById('clear-filters');
    if (clearButton) {
      clearButton.addEventListener('click', this.#handleClearFilters);
    }
    
    // Handle explicit Apply button (type="button") for mobile filters
    this.applyButton = this.querySelector('button[form="mobile-filter-form"]');
    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.#handleApplyClick);
    }
    
      // Handle form submission to prevent page reload
      const mobileForm = document.getElementById('mobile-filter-form');
      if (mobileForm) {
        mobileForm.addEventListener('submit', this.#handleFormSubmit);
        
        // Track when user manually changes the sort select
        const sortSelect = mobileForm.querySelector('select[name="sort_by"]');
        if (sortSelect) {
          // Remove any existing listener to avoid duplicates
          const existingHandler = sortSelect._changeHandler;
          if (existingHandler) {
            sortSelect.removeEventListener('change', existingHandler);
          }
          
          // Create and store the handler
          const changeHandler = () => {
            sortSelect.dataset.userSelected = 'true';
          };
          
          sortSelect._changeHandler = changeHandler;
          sortSelect.addEventListener('change', changeHandler);
        }
      }
  }

  #removeEventListeners() {
    this.toggleButtons.forEach(button => {
      button.removeEventListener('click', this.#handleToggle);
    });

    this.overlay?.removeEventListener('click', this.#handleClose);
    
    const clearButton = document.getElementById('clear-filters');
    if (clearButton) {
      clearButton.removeEventListener('click', this.#handleClearFilters);
    }
    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.#handleApplyClick);
    }
    
    const mobileForm = document.getElementById('mobile-filter-form');
    if (mobileForm) {
      mobileForm.removeEventListener('submit', this.#handleFormSubmit);
    }
  }

  #handleOpen = () => {
    this.isOpen = true;
    this.classList.remove('translate-x-full');
    this.classList.add('translate-x-0');
    document.body.classList.add('overflow-hidden');
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));
    
    // Sync mobile form with current URL when drawer opens
    setTimeout(() => {
      const mobileForm = document.getElementById('mobile-filter-form');
      if (mobileForm) {
        const urlParams = new URLSearchParams(window.location.search);
        const mobileSortSelect = mobileForm.querySelector('select[name="sort_by"]');
        if (mobileSortSelect) {
          this.#syncSortSelectFromUrl(mobileSortSelect, urlParams);
        }
      }
    }, 50);
  }

  #handleClose = () => {
    this.isOpen = false;

    // Remove focus from any focused element inside the drawer
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.classList.remove('translate-x-0');
    this.classList.add('translate-x-full');
    document.body.classList.remove('overflow-hidden');
    // Also hide header overlay via event bus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  }

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  }

  #handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Apply the mobile filters
    this.#applyMobileFilters();
  }

  #handleApplyClick = (e) => {
    e.preventDefault();
    this.#applyMobileFilters();
  }

  #handleClearFilters = () => {
    const form = document.getElementById('mobile-filter-form');
    if (form) {
      // Uncheck all checkboxes
      const checkboxes = form.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      
      // Clear all number inputs
      const numberInputs = form.querySelectorAll('input[type="number"]');
      numberInputs.forEach(input => {
        input.value = '';
      });
      
      // Clear sorting
      const sortSelect = form.querySelector('select[name="sort_by"]');
      if (sortSelect) {
        sortSelect.value = '';
      }
      
      // Apply the cleared filters directly without triggering form submission
      this.#applyMobileFilters();
    }
  }

  #applyMobileFilters = () => {
    // Find the desktop filter form (not the mobile one)
    const allFilterForms = document.querySelectorAll('facet-filters-form');
    const desktopFilterForm = Array.from(allFilterForms).find(formElement => 
      !formElement.closest('mobile-filter-drawer-content')
    );
    const mainFilterForm = desktopFilterForm?.querySelector('form');

    if (!mainFilterForm || mainFilterForm.id === 'mobile-filter-form') return;

    const mobileForm = document.getElementById('mobile-filter-form');
    if (!mobileForm) return;

    // Get mobile sort value directly (more reliable than FormData)
    const mobileSortSelect = mobileForm.querySelector('select[name="sort_by"]');
    const mobileSortValue = mobileSortSelect?.value || '';
    
    // Clear user-selected flag since we're applying filters
    if (mobileSortSelect) {
      delete mobileSortSelect.dataset.userSelected;
    }
    
    const mobileFormData = new FormData(mobileForm);
    const mainSortSelect = this.#getDesktopSortSelect();
    
    // Clear main form
    mainFilterForm.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
    mainFilterForm.querySelectorAll('input[type="number"]').forEach(input => input.value = '');
    if (mainSortSelect) mainSortSelect.value = '';
    
    // Apply mobile form data to main form
    for (const [name, value] of mobileFormData.entries()) {
      if (name === 'sort_by') continue; // Handle separately
      
      const mainInput = mainFilterForm.querySelector(`[name="${name}"][value="${value}"]`);
      if (mainInput) {
        mainInput.checked = true;
      } else {
        const numberInput = mainFilterForm.querySelector(`[name="${name}"]`);
        if (numberInput?.type === 'number') {
          numberInput.value = value;
        }
      }
    }
    
    // Apply mobile sort value to desktop sort select
    if (mainSortSelect && mobileSortValue && this.#isValidSortOption(mainSortSelect, mobileSortValue)) {
      mainSortSelect.value = mobileSortValue;
    }
    
    // Trigger filter change
    const desktopFacetForm = mainFilterForm.closest('facet-filters-form');
    if (desktopFacetForm?.triggerFilterChange) {
      desktopFacetForm.triggerFilterChange();
    } else {
      const firstInput = mainFilterForm.querySelector('input, select');
      firstInput?.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    this.#handleClose();
  }

  #syncOverlayState() {
    const prevTransition = this.style.transition;
    this.style.transition = 'none';
    if (this.isOpen) {
      this.classList.add('translate-x-0');
      this.classList.remove('translate-x-full');
    } else {
      this.classList.remove('translate-x-0');
      this.classList.add('translate-x-full');
    }
    void this.offsetHeight;
    this.style.transition = prevTransition;
  }
}

function initHeaderOverlay() {
  const allDetails = document.querySelectorAll('#header details');
  const overlay = document.querySelector('[data-header-overlay]');

  allDetails.forEach(details => {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.addEventListener('click', () => {
      allDetails.forEach(other => {
        if (other !== details && other.hasAttribute('open')) {
          other.removeAttribute('open');
          ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
        }
      });

      setTimeout(() => {
        const isOpen = details.hasAttribute('open');
        if (isOpen) {
          ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
        } else {
          ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
        }
      }, 10);
    });
  });

  overlay?.addEventListener('click', () => {
    let closed = 0;
    allDetails.forEach((d) => {
      if (d.hasAttribute('open')) {
        d.removeAttribute('open');
        closed += 1;
      }
    });
    for (let i = 0; i < closed; i++) {
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
  });
}

document.addEventListener('DOMContentLoaded', initHeaderOverlay);


document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("cart-drawer-component")) {
    customElements.define("cart-drawer-component", CartDrawerComponent);
  }
  if (!customElements.get("header-drawer-component")) {
    customElements.define("header-drawer-component", HeaderDrawerComponent);
  }
  if (!customElements.get("header-drawer-menu")) {
    customElements.define("header-drawer-menu", HeaderDrawerMenu);
  }
  if (!customElements.get("mobile-filter-drawer-component")) {
    customElements.define("mobile-filter-drawer-component", MobileFilterDrawerComponent);
  }
  if (!customElements.get("modal-drawer-component")) {
    customElements.define("modal-drawer-component", ModalDrawerComponent);
  }
  // Keep the header cart bubble in sync when cart changes
  ThemeBus.addEventListener(EVENTS.cartUpdated, () => updateCartBubble());
  ThemeBus.addEventListener(EVENTS.cartItemAdded, () => updateCartBubble());
});

// Lazy loading opacity handler
class LazyImageHandler {
  constructor() {
    this.init();
  }

  init() {
    // Handle existing lazy images
    this.handleLazyImages();
    
    // Handle new lazy images (for dynamic content)
    document.addEventListener('shopify:section:load', () => {
      this.handleLazyImages();
    });
  }

  handleLazyImages() {
    const lazyImages = document.querySelectorAll('.content-image img[loading="lazy"]');
    
    lazyImages.forEach(img => {
      // Skip if already processed
      if (img.dataset.lazyProcessed) return;
      
      // Mark as processed
      img.dataset.lazyProcessed = 'true';
      
      // Set initial opacity to 0 for transition
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s ease-in-out';
      
      // Handle load event
      img.addEventListener('load', () => {
        img.style.opacity = '1';
      });
      
      // Handle error event
      img.addEventListener('error', () => {
        img.style.opacity = '1'; // Show even if error
      });
      
      // Fallback: if image is already loaded
      if (img.complete && img.naturalHeight !== 0) {
        img.style.opacity = '1';
      }
    });
  }
}

// Initialize lazy image handler
document.addEventListener('DOMContentLoaded', () => {
  new LazyImageHandler();
  initHeaderOverlayController();
  initDrawerOverlayController();
});


// Export for global access
window.LazyImageHandler = LazyImageHandler;

/**
 * Helper: Get default sort value (prefers 'manual', falls back to first option)
 */
function getDefaultSortValue(sortSelect) {
  if (!sortSelect || sortSelect.options.length === 0) return null;
  
  // Try to find 'manual' (featured) option first
  const manualOption = Array.from(sortSelect.options).find(opt => opt.value === 'manual');
  if (manualOption) return 'manual';
  
  // Fall back to first option
  return sortSelect.options[0].value;
}

/**
 * Helper: Sync sort select from URL (used by updateMobileFilters)
 */
function syncSortSelectFromUrl(sortSelect, urlParams) {
  if (!sortSelect) return;

  const sortValue = urlParams.get('sort_by');
  const currentValue = sortSelect.value;
  const userSelected = sortSelect.dataset.userSelected === 'true';

  // Preserve user selection if manually selected and not in URL yet
  if (userSelected && currentValue && !sortValue) return;

  if (sortValue) {
    const optionExists = Array.from(sortSelect.options).some(opt => opt.value === sortValue);
    if (optionExists && sortValue !== currentValue) {
      sortSelect.value = sortValue;
      delete sortSelect.dataset.userSelected;
    }
  } else if (!userSelected && (!currentValue || currentValue === '')) {
    // Set default (prefers 'manual', falls back to first option)
    const defaultValue = getDefaultSortValue(sortSelect);
    if (defaultValue) {
      sortSelect.value = defaultValue;
    }
  }
}

// Sync mobile filter drawer form with current URL (centralized)
function updateMobileFilters() {
  const mobileForm = document.getElementById('mobile-filter-form');
  if (!mobileForm) return;

  const urlParams = new URLSearchParams(window.location.search);

  // Sync checkboxes
  mobileForm.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
    const paramName = checkbox.name;
    const paramValue = checkbox.value;
    checkbox.checked = urlParams.has(paramName) && urlParams.get(paramName) === paramValue;
  });

  // Sync number inputs
  mobileForm.querySelectorAll('input[type="number"]').forEach((input) => {
    input.value = urlParams.get(input.name) || '';
  });

  // Sync sort select
  const mobileSortSelect = mobileForm.querySelector('select[name="sort_by"]');
  syncSortSelectFromUrl(mobileSortSelect, urlParams);
}

document.addEventListener('DOMContentLoaded', updateMobileFilters);
ThemeBus.addEventListener(EVENTS.filtersUpdated, updateMobileFilters);

// Account modal toggle (delegated, consistent with existing structure)
document.addEventListener('DOMContentLoaded', () => {
  const handleToggle = (e) => {
    const toggleEl = e.target.closest('[data-account-modal-toggle]');
    if (!toggleEl) return;
    e.preventDefault();
    const modal = document.querySelector('[data-account-modal]');
    if (!modal) return;

    const isHidden = modal.classList.contains('hidden');
    if (isHidden) {
      // Prepare for fade/slide in
      modal.classList.remove('hidden');
      modal.classList.add('opacity-0');
      // Next frame animate to visible
      requestAnimationFrame(() => {
        modal.classList.remove('opacity-0');
        modal.classList.add('opacity-100');
      });
      document.body.classList.add('overflow-hidden');
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
    } else {
      // Animate out then hide on transition end
      const onEnd = () => {
        modal.classList.add('hidden');
        modal.removeEventListener('transitionend', onEnd);
      };
      modal.addEventListener('transitionend', onEnd);
      modal.classList.remove('opacity-100');
      modal.classList.add('opacity-0');
      document.body.classList.remove('overflow-hidden');
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
  };

  // Delegate clicks for any current or future toggle buttons
  document.addEventListener('click', handleToggle);

  // Close when clicking outside the modal
  document.addEventListener('click', (e) => {
    const modal = document.querySelector('[data-account-modal]');
    if (!modal) return;

    // Ignore clicks on toggles (open/close buttons)
    if (e.target.closest('[data-account-modal-toggle]')) return;

    // Only act when modal is visible
    if (modal.classList.contains('hidden')) return;

    // If click is outside modal, close it
    if (!e.target.closest('[data-account-modal]')) {
      // Animate out then hide
      const onEnd = () => {
        modal.classList.add('hidden');
        modal.removeEventListener('transitionend', onEnd);
      };
      modal.addEventListener('transitionend', onEnd);
      modal.classList.remove('opacity-100');
      modal.classList.add('opacity-0');
      document.body.classList.remove('overflow-hidden');
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const modal = document.querySelector('[data-account-modal]');
    if (!modal) return;
    if (!modal.classList.contains('hidden')) {
      const onEnd = () => {
        modal.classList.add('hidden');
        modal.removeEventListener('transitionend', onEnd);
      };
      modal.addEventListener('transitionend', onEnd);
      modal.classList.remove('opacity-100');
      modal.classList.add('opacity-0');
      document.body.classList.remove('overflow-hidden');
        ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
  });
});

class LocalizationForm extends Component {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="country_code"]'),
      button: this.querySelector('.localization-selector__button'),
      panel: this.querySelector('.localization-selector__list'),
    };

    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  hidePanel() {
    // Only trigger event if panel is actually visible (going from open to closed)
    const wasVisible = !this.elements.panel.hasAttribute('hidden') && !this.elements.panel.classList.contains('hidden');
    
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);
    this.elements.panel.classList.add('hidden');
    
    // Only dispatch closed event if panel was actually visible
    if (wasVisible) {
      ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayHide));
    }
    // Legacy header:setInactive removed in favor of ThemeBus overlay events
  }

  showPanel() {
    this.elements.button.setAttribute('aria-expanded', 'true');
    this.elements.panel.removeAttribute('hidden');
    this.elements.panel.classList.remove('hidden');
    ThemeBus.dispatchEvent(new Event(EVENTS.headerOverlayShow));
    // Legacy header:setActive removed in favor of ThemeBus overlay events
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && !this.contains(event.relatedTarget);
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }

  onDocumentClick(event) {
    // Only close if clicking outside and panel is visible
    const isVisible = !this.elements.panel.hasAttribute('hidden') && !this.elements.panel.classList.contains('hidden');
    if (isVisible && !this.contains(event.target)) {
      this.hidePanel();
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("localization-form")) {
    customElements.define("localization-form", LocalizationForm);
  }
  // Inventory status updater using ThemeBus variant events
  try {
    const updateInventoryStatus = (max, outOfStock, preorder) => {
      const qty = Number.isFinite(max) ? Number(max) : max; // Infinity stays Infinity
      const showOut = !!outOfStock || qty <= 0;
      const isPreorder = !!preorder;

      document.querySelectorAll('inventory-status').forEach((el) => {
        // Read threshold from data attribute, default to 5 if not set
        const threshold = Number(el.dataset.lowStockThreshold) || 5;
        const showLow = !isPreorder && !showOut && qty > 0 && qty <= threshold;
        const showIn = !isPreorder && !showOut && !showLow && qty > 0;

        const preorderEl = el.querySelector('.inventory-status-preorder');
        const inEl = el.querySelector('.inventory-status-in-stock');
        const lowEl = el.querySelector('.inventory-status-low-stock');
        const outEl = el.querySelector('.inventory-status-out-of-stock');
        if (preorderEl) preorderEl.classList.toggle('hidden', !isPreorder);
        if (inEl) inEl.classList.toggle('hidden', !showIn);
        if (lowEl) lowEl.classList.toggle('hidden', !showLow);
        if (outEl) outEl.classList.toggle('hidden', !showOut);
      });
    };
    ThemeBus.addEventListener(EVENTS.variantInventory, (e) => {
      const max = Number(e.detail?.max ?? 0);
      const preorder = e.detail?.preorder === true;
      updateInventoryStatus(max, false, preorder);
    });
    ThemeBus.addEventListener(EVENTS.variantOutOfStock, () => {
      updateInventoryStatus(0, true, false);
    });
  } catch (_) {}

  // Selected value updater using ThemeBus variant events
  try {
    ThemeBus.addEventListener(EVENTS.variantChange, (e) => {
      const variant = e.detail?.variant;
      if (!variant || !variant.options) return;

      variant.options.forEach((value, index) => {
        document.querySelectorAll(`[data-selected-value="${index}"]`).forEach((el) => {
          el.textContent = value;
        });
      });
    });
  } catch (_) {}

  // Variant SKU updater using ThemeBus variant events
  try {
    ThemeBus.addEventListener(EVENTS.variantChange, (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      const sku = typeof variant.sku === 'string' ? variant.sku.trim() : '';

      document.querySelectorAll('variant-sku').forEach((el) => {
        const valueEl = el.querySelector('.variant-sku-value');
        if (valueEl) valueEl.textContent = sku;
        el.classList.toggle('hidden', sku === '');
      });
    });
  } catch (_) {}

  // Search icon click handler
  try {
    const searchIconButton = document.querySelector('[ref="searchModalToggle"]');
    if (searchIconButton) {
      searchIconButton.addEventListener('click', () => {
        ThemeBus.dispatchEvent(new Event(EVENTS.predictiveSearchOpen));
      });
    }
  } catch (_) {}
});